import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener('change', this.#handleChange);
    document.addEventListener('cart:update', this.#handleCartUpdate);
    window.visualViewport?.addEventListener('resize', this.#syncViewportInsets);
    window.visualViewport?.addEventListener('scroll', this.#syncViewportInsets);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener('change', this.#handleChange);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
    window.visualViewport?.removeEventListener('resize', this.#syncViewportInsets);
    window.visualViewport?.removeEventListener('scroll', this.#syncViewportInsets);
  }

  /** @param {Event} e */
  #handleChange = (e) => {
    const toggle = /** @type {HTMLInputElement} */ (e.target);
    if (!toggle.matches?.('.cart-expedite__toggle')) return;
    const card = toggle.closest('.cart-expedite');
    if (!card) return;
    const variantId = card.dataset.expediteVariantId;
    const sectionId = card.dataset.expediteSectionId || '';
    if (!variantId) return;

    const cartAddUrl = (this.dataset.cartAddUrl || window.Theme?.routes?.cart_add_url || '/cart/add');
    const addUrl = cartAddUrl.includes('.js') ? cartAddUrl : `${cartAddUrl}.js`;
    const cartUrl = (this.dataset.cartUrl || window.Theme?.routes?.cart_url || '/cart');
    const cartUrlJson = cartUrl.includes('.js') ? cartUrl : `${cartUrl}.js`;
    const cartChangeUrl = this.dataset.cartChangeUrl || window.Theme?.routes?.cart_change_url || '/cart/change';

    const setBusy = (busy) => {
      toggle.disabled = busy;
      card.classList.toggle('cart-expedite--busy', busy);
    };
    const setActive = (active) => {
      card.classList.toggle('cart-expedite--active', active);
      const badge = card.querySelector('.cart-expedite__badge');
      if (badge) {
        badge.textContent = active ? 'Aktif · Öncelikli' : 'Öncelikli üretim';
      }
    };
    const refreshSection = () => {
      if (!sectionId || typeof sectionRenderer?.renderSection !== 'function') return;
      sectionRenderer.renderSection(sectionId, { cache: false });
    };

    setBusy(true);
    setActive(toggle.checked);
    if (toggle.checked) {
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10) || variantId, quantity: 1 }],
          sections: sectionId ? sectionId : undefined,
        }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.status && data.status !== 200) {
            toggle.checked = false;
            setActive(false);
            if (data.message) console.warn(data.message);
          } else {
            if (data.sections?.[sectionId] && typeof morphSection === 'function') {
              morphSection(sectionId, data.sections[sectionId]);
            } else {
              refreshSection();
            }
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: { resource: data, sourceId: 'cart-expedite', data: { sections: data.sections } },
              })
            );
          }
        })
        .catch(() => {
          toggle.checked = false;
          setActive(false);
        })
        .finally(() => setBusy(false));
    } else {
      fetch(cartUrlJson)
        .then((r) => r.json())
        .then((cart) => {
          let lineNum = 0;
          if (cart?.items?.length) {
            for (let i = 0; i < cart.items.length; i++) {
              if (String(cart.items[i].variant_id) === String(variantId)) {
                lineNum = i + 1;
                break;
              }
            }
          }
          if (lineNum === 0) {
            setBusy(false);
            return;
          }
          return fetch(cartChangeUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              line: lineNum,
              quantity: 0,
              sections: sectionId || undefined,
              sections_url: window.location.pathname,
            }),
          }).then((r) => r.json());
        })
        .then((data) => {
          if (!data) return;
          if (data.sections?.[sectionId] && typeof morphSection === 'function') {
            morphSection(sectionId, data.sections[sectionId]);
          } else {
            refreshSection();
          }
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: { resource: data, sourceId: 'cart-expedite', data: { sections: data.sections } },
            })
          );
        })
        .catch(() => {
          toggle.checked = true;
          setActive(true);
        })
        .finally(() => setBusy(false));
    }
  };

  /** @param {CustomEvent & { detail?: { resource?: { items?: { variant_id: number }[] } } }} e */
  #handleCartUpdate = (e) => {
    const resource = e.detail?.resource;
    if (!resource) return;
    const toggle = this.querySelector('.cart-expedite__toggle');
    const card = this.querySelector('.cart-expedite');
    if (!toggle || !card) return;
    const variantId = card.dataset.expediteVariantId;
    if (!variantId) return;
    const inCart = (resource.items || []).some((item) => String(item.variant_id) === String(variantId));
    toggle.checked = inCart;
    card.classList.toggle('cart-expedite--active', inCart);
    const badge = card.querySelector('.cart-expedite__badge');
    if (badge) {
      badge.textContent = inCart ? 'Aktif · Öncelikli' : 'Öncelikli üretim';
    }
  };

  #handleCartAdd = () => {
    if (!this.hasAttribute('auto-open')) return;

    // Let cart-items morph finish before opening so the dialog is not left in a broken modal state.
    requestAnimationFrame(() => {
      this.showDialog();
      this.#updateStickyState();
    });
  };

  open() {
    this.showDialog();
    this.#updateStickyState();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Keep drawer within the visible viewport (Instagram / Meta in-app browsers report inflated 100vh).
   */
  #syncViewportInsets = () => {
    const dialog = this.refs?.dialog;
    if (!dialog) return;

    const vv = window.visualViewport;
    if (!vv) {
      dialog.style.removeProperty('max-height');
      dialog.style.removeProperty('height');
      return;
    }

    // Size to visual viewport only — do not also add chrome as padding (would double-count).
    const visibleHeight = Math.round(vv.height);
    dialog.style.maxHeight = `${visibleHeight}px`;
    dialog.style.height = `${visibleHeight}px`;
  };

  #updateStickyState() {
    // Drawer uses a fixed footer; keep height aligned to visualViewport for in-app browsers.
    this.#syncViewportInsets();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
